import * as THREE from 'three';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { FikoGrup } from './FikoGrup';
import { FikoSahne } from './FikoSahne';
import { KaliciOlcumlerDialogComponent } from './fiko-olcum/kalici-olcumler-dialog/kalici-olcumler-dialog.component';
import { OlcumDuzenleDialogComponent } from './fiko-olcum/olcum-duzenle-dialog/olcum-duzenle-dialog.component';

/**
 * Kalıcı ölçüm gösterimlerini içeren grup sınıfı
 * Ürün ağacında "Kaynakça > Ölçümler" grubu olarak görünür
 * Dialog kapatılsa bile ölçümler silinmez
 *
 * 🎯 DRAG FEATURE: Ölçüm objelerini sahnede sürükleyip taşıma desteği
 */
export class FikoKaliciOlcumler extends FikoGrup {
  override type = 'FikoKaliciOlcumler';

  private olcumSayisi: number = 0;

  // 🎯 KAYDET/YÜKLE: kalıcı ölçümleri yeniden kurmak için serialize-edilebilir kayıtlar (nokta/tip/değer)
  olcumKayitlari: any[] = [];

  /** Kaydetme için kalıcı ölçüm kayıtlarını döndürür. */
  kayitlariAl(): any[] { return this.olcumKayitlari; }

  // 🎯 DRAG: TransformControls instance
  private transformControls: TransformControls | null = null;
  private aktifSuruklenenOlcum: THREE.Group | null = null;

  // 🎯 v3.2.3: Sabit boyut sistemi - kamera event listener
  private kameraListenerEklendi: boolean = false;

  // ⚡ PERFORMANS: RAF throttle + dirty flag + sprite cache
  private _sabitBoyutDirty = false;
  private _sabitBoyutRafId: number | null = null;
  private _sonScaleAdjustment = -1;
  private _cachedSprites: { sprite: THREE.Sprite, origScale: THREE.Vector3 }[] = [];
  private _spriteCacheDirty = true;

  private kameraChangeEvent = () => {
    // ⚡ Dirty flag: sadece bir kez RAF planla, tekrar eden event'leri atla
    if (!this._sabitBoyutDirty) {
      this._sabitBoyutDirty = true;
      if (this._sabitBoyutRafId === null) {
        this._sabitBoyutRafId = requestAnimationFrame(() => {
          this._sabitBoyutRafId = null;
          this._sabitBoyutDirty = false;
          this.tumOlcumlereSabitBoyutUygula();
        });
      }
    }
  };

  constructor(public override sahne?: FikoSahne) {
    super(sahne, 'Kalıcı Ölçümler');

    // UI Ayarları
    this.userData['FikoArayuz'] = {
      agactaGorunur: true,
      listeGoster: true,
      simge: './simgeler/coklu-cetvel.png',
      baslik: 'Kalıcı Ölçümler',
      renk: '#FFD700' // CATIA tarzı altın sarısı
    };

    // Render order - ölçümler en üstte
    this.renderOrder = 999998;

    // 🎯 Sağ tık menüsü
    (this as any).sagMenu = [
      {
        simgeYazi: '📋',
        baslik: 'Ölçümleri Yönet',
        tiklandi: () => this.olcumYonetDialoguAc()
      },
      {
        simgeYazi: '👁‍🗨',
        baslik: 'Tümünü Göster/Gizle',
        tiklandi: () => this.tumunuGosterGizle()
      },
      { tipi: 'Ayraç' },
      {
        simgeYazi: '🗑️',
        baslik: 'Tümünü Sil',
        tiklandi: () => this.tumunuSilOnay()
      }
    ];

    // 🎯 v3.2.3: Kamera event listener'ı ekle
    this.kameraListenerBaslat();
  }

  /**
   * 🎯 v3.2.3: Kamera değişikliklerini dinle - sabit boyut için
   */
  private kameraListenerBaslat(): void {
    if (this.kameraListenerEklendi) return;
    if (!this.sahne?.anaGosterim?.aktifKontrol) {
      // Kamera henüz hazır değil, biraz bekle
      setTimeout(() => this.kameraListenerBaslat(), 500);
      return;
    }

    this.sahne.anaGosterim.aktifKontrol.addEventListener('change', this.kameraChangeEvent);
    this.kameraListenerEklendi = true;
    // console.log('[FikoKaliciOlcumler] Kamera listener eklendi');
  }

  /**
   * ⚡ PERFORMANS: Sprite listesini guncelle (traverse yerine flat liste)
   * Sadece olcum ekleme/silme sonrasi cagirilir
   */
  private _spriteListeGuncelle(): void {
    if (!this._spriteCacheDirty) return;
    this._cachedSprites.length = 0;
    for (let i = 0; i < this.children.length; i++) {
      const olcumGrubu = this.children[i];
      if (olcumGrubu.userData.sabitBoyut === false) continue;
      olcumGrubu.traverse((child) => {
        if (child instanceof THREE.Sprite && child.userData.originalScale) {
          this._cachedSprites.push({
            sprite: child,
            origScale: child.userData.originalScale as THREE.Vector3
          });
        }
      });
    }
    this._spriteCacheDirty = false;
  }

  /**
   * 🎯 v3.2.3: Tüm ölçümlere sabit boyut uygula
   * ⚡ PERFORMANS: RAF throttle + flat cache + scale delta check
   */
  private tumOlcumlereSabitBoyutUygula(): void {
    if (!this.sahne?.anaGosterim?.aktifKamera) return;

    const kamera = this.sahne.anaGosterim.aktifKamera as any;
    const zoom = kamera?.zoom ?? 1;
    const factor = ((kamera?.top ?? 100) - (kamera?.bottom ?? -100)) / zoom;
    const scaleAdjustment = factor * 0.02;

    // ⚡ Scale degismemisse atla
    if (Math.abs(this._sonScaleAdjustment - scaleAdjustment) < 0.0001) return;
    this._sonScaleAdjustment = scaleAdjustment;

    // ⚡ Flat sprite cache kullan (traverse yok)
    this._spriteListeGuncelle();

    for (let i = 0; i < this._cachedSprites.length; i++) {
      const entry = this._cachedSprites[i];
      entry.sprite.scale.set(
        entry.origScale.x * scaleAdjustment,
        entry.origScale.y * scaleAdjustment,
        1
      );
    }
  }

  /**
   * Ölçüm yönetim dialog'unu aç
   */
  private olcumYonetDialoguAc(): void {
    if (!this.sahne?.anaGosterim?.DialogFiko) {
      console.warn('[FikoKaliciOlcumler] DialogFiko bulunamadı');
      return;
    }

    this.sahne.anaGosterim.DialogFiko.ac({
      bilesen: KaliciOlcumlerDialogComponent,
      parametreler: {
        kaliciOlcumler: this,
        anaGosterim: this.sahne.anaGosterim
      },
      ayarlar: {
        baslik: 'Kalıcı Ölçümler',
        simgeResim: './simgeler/coklu-cetvel.png',
        genislik: 500,
        yukseklik: 450
      }
    });
  }

  /**
   * Tüm ölçümlerin görünürlüğünü toggle et
   */
  private tumunuGosterGizle(): void {
    // Hepsi görünür mü kontrol et
    const hepsiGorunur = this.children.every(child => child.visible);

    // Toggle
    this.children.forEach(child => {
      child.visible = !hepsiGorunur;
    });

    // Sahneyi yenile
    this.sahne?.anaGosterim?.ciz();
  }

  /**
   * Tümünü sil (onay ile)
   */
  private tumunuSilOnay(): void {
    if (this.children.length === 0) return;

    if (confirm(`${this.children.length} kalıcı ölçümü silmek istediğinizden emin misiniz?`)) {
      this.temizle();
      this.sahne?.anaGosterim?.ciz();
    }
  }

  /**
   * Kalıcı ölçüm ekle
   * @param olcumGrubu Ölçüm THREE.Group objesi
   * @param baslik Ölçüm başlığı (örn: "123.45 mm")
   * @param sessionId Dialog session ID
   */
  kaliciOlcumEkle(olcumGrubu: THREE.Group, baslik: string, sessionId?: string): void {
    // 🎯 KAYDET/YÜKLE: ölçümün PARAMETRELERİNİ (nokta/tip/değer) kaydet → yüklemede AYNI constructor ile
    // gerçek ölçüm objesi üretilir (sabit-boyut etiket + birebir görünüm). klonGrup.userData kumpas'tan gelir.
    try {
      const ud: any = olcumGrubu.userData || {};
      const arr = (v: any) => v?.toArray ? v.toArray() : (v ? [v.x, v.y, v.z] : null);
      const n1 = arr(ud.startPoint), n2 = arr(ud.endPoint);
      if (n1 && n2) {
        const tip = ud.olcumTipi === 'cap' ? 'cap' : (ud.olcumTipi === 'alan' ? 'alan' : 'mesafe');
        const rec: any = { tip, n1, n2, baslik, deger: ud.gercekDeger, birim: 'mm', hassasiyet: 2 };
        if (tip === 'cap') {
          rec.merkez = (ud.merkez && arr(ud.merkez)) || [(n1[0] + n2[0]) / 2, (n1[1] + n2[1]) / 2, (n1[2] + n2[2]) / 2];
          const d = Math.hypot(n1[0] - n2[0], n1[1] - n2[1], n1[2] - n2[2]);
          rec.yaricap = ud.yaricap || ud.radius || (ud.cap ? ud.cap / 2 : d / 2);
        }
        this.olcumKayitlari.push(rec);
      }
    } catch (e) { /* sessiz */ }

    // Ölçüm grubunu adlandır
    const olcumNo = ++this.olcumSayisi;
    const zaman = new Date();
    olcumGrubu.name = `Ölçüm_${olcumNo}_${baslik}`;

    // Ölçüm verilerini userData'da tut
    olcumGrubu.userData = {
      ...olcumGrubu.userData,
      FikoArayuz: {
        agactaGorunur: true,
        simge: './simgeler/cetvel.png',
        baslik: baslik,
        renk: '#22c55e'
      },
      olcumNo: olcumNo,
      olcumBaslik: baslik,
      sessionId: sessionId,
      kalici: true,
      olusturmaTarihi: zaman
    };

    // 🎯 Her ölçüm için sağ tık menüsü
    (olcumGrubu as any).sagMenu = [
      {
        simgeYazi: '✏️',
        baslik: 'Düzenle',
        tiklandi: () => this.olcumDuzenleDialoguAc(olcumGrubu, baslik)
      },
      {
        simgeYazi: '👁‍🗨',
        baslik: 'Göster/Gizle',
        tiklandi: () => {
          olcumGrubu.visible = !olcumGrubu.visible;
          this.sahne?.anaGosterim?.ciz();
        }
      },
      {
        simgeYazi: '🎯',
        baslik: 'Odakla',
        tiklandi: () => this.olcumuOdakla(olcumGrubu)
      },
      { tipi: 'Ayraç' },
      {
        simgeYazi: '🗑️',
        baslik: 'Sil',
        tiklandi: () => {
          if (confirm(`"${baslik}" ölçümünü silmek istediğinizden emin misiniz?`)) {
            this.olcumSil(olcumGrubu);
            this.sahne?.anaGosterim?.ciz();
          }
        }
      }
    ];

    // Sahneye ekle
    this.add(olcumGrubu);

    // 🎯 v3.2.3: Sabit boyut varsayılan olarak açık
    olcumGrubu.userData.sabitBoyut = true;

    // ⚡ Sprite cache'i invalidate et (yeni olcum eklendi)
    this._spriteCacheDirty = true;
    this._sonScaleAdjustment = -1; // Force recalculation

    // 🎯 v3.2.3: İlk scale'i uygula (eklendikten hemen sonra)
    this.olcumeIlkScaleUygula(olcumGrubu);

    // 🎯 Ürün ağacında görünmesi için listeGoster'i aç
    if (!this.userData['FikoArayuz'].listeGoster) {
      this.userData['FikoArayuz'].listeGoster = true;
    }

    // console.log('[FikoKaliciOlcumler] Ölçüm eklendi:', {
    //   baslik,
    //   childCount: this.children.length,
    //   parent: this.parent?.name || 'yok',
    //   listeGoster: this.userData['FikoArayuz'].listeGoster,
    //   sabitBoyut: olcumGrubu.userData.sabitBoyut
    // });
  }

  /**
   * 🎯 v3.2.3: Yeni eklenen ölçüme ilk scale'i uygula
   */
  private olcumeIlkScaleUygula(olcumGrubu: THREE.Object3D): void {
    if (!this.sahne?.anaGosterim?.aktifKamera) return;

    const kamera = this.sahne.anaGosterim.aktifKamera as any;
    const zoom = kamera?.zoom ?? 1;
    const factor = ((kamera?.top ?? 100) - (kamera?.bottom ?? -100)) / zoom;
    const scaleAdjustment = factor * 0.02;

    olcumGrubu.traverse((child) => {
      if (child instanceof THREE.Sprite && child.userData.originalScale) {
        const orig = child.userData.originalScale as THREE.Vector3;
        child.scale.set(
          orig.x * scaleAdjustment,
          orig.y * scaleAdjustment,
          1
        );
      }
    });
  }

  /**
   * Ölçüm düzenleme dialog'unu aç
   */
  private olcumDuzenleDialoguAc(olcumGrubu: THREE.Group, baslik: string): void {
    if (!this.sahne?.anaGosterim?.DialogFiko) {
      console.warn('[FikoKaliciOlcumler] DialogFiko bulunamadı');
      return;
    }

    this.sahne.anaGosterim.DialogFiko.ac({
      bilesen: OlcumDuzenleDialogComponent,
      parametreler: {
        olcumGrubu: olcumGrubu,
        anaGosterim: this.sahne.anaGosterim
      },
      ayarlar: {
        baslik: `Ölçüm Düzenle: ${baslik}`,
        simgeResim: './simgeler/cetvel.png',
        genislik: 380,
        yukseklik: 520
      }
    });
  }

  /**
   * Ölçümü 3D'de odakla
   */
  private olcumuOdakla(olcumGrubu: THREE.Group): void {
    if (!this.sahne?.anaGosterim) return;

    // Bounding box hesapla
    const box = new THREE.Box3().setFromObject(olcumGrubu);
    const center = box.getCenter(new THREE.Vector3());

    // Kamerayı odakla
    if (this.sahne.anaGosterim.aktifKontrol) {
      this.sahne.anaGosterim.aktifKontrol.target.copy(center);
      this.sahne.anaGosterim.aktifKontrol.update();
    }

    this.sahne.anaGosterim.ciz();
  }

  /**
   * Session'a ait tüm ölçümleri al
   */
  sessionOlcumleriAl(sessionId: string): THREE.Group[] {
    return this.children.filter(
      child => child.userData?.sessionId === sessionId
    ) as THREE.Group[];
  }

  /**
   * Belirli bir ölçümü sil
   */
  olcumSil(olcumGrubu: THREE.Group): void {
    if (!olcumGrubu) return;

    // 🎯 kaydı da kaldır (tekrar kaydetmede silinen ölçüm geri gelmesin)
    try {
      const rec = (olcumGrubu as any).userData?.olcumKayit || (olcumGrubu as any).userData;
      if (this.olcumKayitlari?.length) {
        const bx = (olcumGrubu as any).userData?.olcumBaslik || (olcumGrubu as any).userData?.FikoArayuz?.baslik;
        if (bx) this.olcumKayitlari = this.olcumKayitlari.filter(r => r.baslik !== bx);
      }
    } catch (e) { /* sessiz */ }

    this.remove(olcumGrubu);
    if (olcumGrubu.parent) olcumGrubu.parent.remove(olcumGrubu);   // başka parent'a bağlıysa da kaldır
    this._spriteCacheDirty = true;

    // 🎯 TÜM alt obje tiplerini (Points/Sprite/Line2/LineSegments/Mesh) dispose et — eksik temizlik
    // "silince noktalar kalıyor" bug'ına karşı: hiçbir alt geometri/materyal/texture sahnede kalmasın.
    olcumGrubu.traverse((child: any) => {
      if (child.geometry?.dispose) child.geometry.dispose();
      const mat = child.material;
      if (mat) {
        const mats = Array.isArray(mat) ? mat : [mat];
        for (const m of mats) { try { m.map?.dispose?.(); } catch (e) {} m.dispose?.(); }
      }
    });

    if (this.sahne?.anaGosterim?.ciz) this.sahne.anaGosterim.ciz();
  }

  /**
   * Son ölçümü sil
   */
  sonOlcumuSil(): void {
    if (this.children.length > 0) {
      const sonOlcum = this.children[this.children.length - 1] as THREE.Group;
      this.olcumSil(sonOlcum);
    }
  }

  /**
   * Tüm ölçümleri temizle
   */
  temizle(): void {
    const children = [...this.children];
    children.forEach((olcum) => {
      if (olcum instanceof THREE.Group) {
        olcum.traverse((child) => {
          if (child instanceof THREE.Mesh || child instanceof THREE.Line) {
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(m => m.dispose());
              } else {
                child.material.dispose();
              }
            }
          }
        });
      }
    });

    this.clear();
    this.olcumSayisi = 0;
    // ⚡ Sprite cache'i invalidate et
    this._spriteCacheDirty = true;
    this._cachedSprites.length = 0;
  }

  /**
   * Ölçüm sayısını al
   */
  olcumSayisiniAl(): number {
    return this.children.length;
  }

  /**
   * Dispose - tam temizlik
   */
  dispose(): void {
    // ⚡ RAF temizle
    if (this._sabitBoyutRafId !== null) {
      cancelAnimationFrame(this._sabitBoyutRafId);
      this._sabitBoyutRafId = null;
    }
    // Kamera listener'i kaldir
    if (this.kameraListenerEklendi && this.sahne?.anaGosterim?.aktifKontrol) {
      this.sahne.anaGosterim.aktifKontrol.removeEventListener('change', this.kameraChangeEvent);
      this.kameraListenerEklendi = false;
    }
    this._cachedSprites.length = 0;
    this.temizle();
  }
}
